import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import { selectCurrency, selectPartnerId } from 'legacy/lut/features/partner360/configuration'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { PARTNER360_CHART_FONT_FAMILY } from 'partner360/configs/typography'
import { P360Currency } from 'partner360/models/P360Currency'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../configs/translations'
import { useMdfAnalysis } from '../../hooks/useMdfAnalysis'

import { getMdfStatusFunnelGroups, TFunnelCategory } from './helpers/getMdfStatusFunnelGroups'

import styles from './MdfActivitiesStatusFunnel.module.scss'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.dashboard.statusFunnel

// Bar colors mirror the StatusBadge palette (slate → blue → teal → green).
const CATEGORY_COLOR: Record<TFunnelCategory, string> = {
  Planning: '#64748b',
  Execution: '#0a63ac',
  Payment: '#009b9f',
  Paid: '#2dbf6f',
}

export const MdfActivitiesStatusFunnel = () => {
  const partnerId = useSelector(selectPartnerId)
  const { filteredActivities: activities } = useMdfAnalysis(partnerId)
  const activeCurrency = useSelector(selectCurrency)
  const isUsd = activeCurrency === P360Currency.USD

  const groups = useMemo(() => getMdfStatusFunnelGroups(activities, isUsd), [activities, isUsd])

  const option = useMemo<EChartsOption>(() => {
    const formatValue = (value: number) =>
      getFormattedFinance(value, { withCurrencySign: isUsd, maximumFractionDigits: 0 })

    return {
      textStyle: { fontFamily: PARTNER360_CHART_FONT_FAMILY },

      grid: {
        left: 16,
        right: 24,
        top: 24,
        bottom: 16,
        containLabel: true,
      },

      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        valueFormatter: (value: any) => formatValue(Number(value)),
      },

      xAxis: {
        type: 'value',
        axisLabel: {
          color: '#666',
          fontSize: 14,
          formatter: (value: number) => formatValue(value),
        },
        splitLine: {
          lineStyle: {
            color: '#d9d9d9',
          },
        },
      },

      yAxis: {
        type: 'category',
        inverse: true,
        data: groups.map((group) => group.label),
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
        },
      },

      series: [
        {
          type: 'bar',
          barWidth: '60%',
          data: groups.map((group) => ({
            value: group.requested,
            itemStyle: {
              color: CATEGORY_COLOR[group.category],
            },
          })),
        },
      ],
    }
  }, [groups, isUsd])

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h3 className={styles.title}>
            <Translation text={KEYS.title} span />
          </h3>
          <div className={styles.subtitle}>
            <Translation text={KEYS.subtitle} span />
          </div>
        </div>
      </div>

      <div className={styles.chart}>
        <ReactEcharts option={option} opts={{ renderer: 'svg' }} />
      </div>
    </div>
  )
}

MdfActivitiesStatusFunnel.displayName = 'MdfActivitiesStatusFunnel'
